import type { Tier } from '@/state/GameState';
import type { ContractStatus } from '@/data/types';

export const GAME_WIDTH = 1080;
export const GAME_HEIGHT = 1920;

// Standard-bredde for fullskjerm-modaler (runde 36). Mindre dialoger
// (confirm-bokser, EventCard, FloorUpgradeModal) bruker egne bredder.
export const MODAL_BREDDE = 960;

// Runde 45: normalisert modal-padding etter persona-tilbakemelding.
// Tidligere varierte verdier mellom 16/24/28/32/40 px på tvers av ~20
// modaler — Lars (UX) og Karin (visual) trakk for inkonsistens. Vi
// standardiserer:
// - MODAL_PADDING (32): hovedmargin innenfor modal-sheet
// - KORT_PADDING  (16): tett-pakkede kort/rader inni en modal
// Avvik dokumenteres lokalt med kommentar (f.eks. NavnInputModal som
// trenger romsligere padding rundt input-feltet).
export const MODAL_PADDING = 32;
export const KORT_PADDING = 16;

export const FORSKUDD_PER_TIER: Record<Tier, number> = {
  D: 20_000,
  C: 40_000,
  B: 80_000,
  A: 140_000,
  S: 220_000,
};

// Runde 48 (Sven): startkapital senket fra 200 000 til 120 000.
export const STARTKAPITAL = 120_000;

export const BASE_SALG = 50;
export const ROYALTY_PER_SALG = 150;

export const TIER_SALG_FAKTOR: Record<Tier, number> = {
  D: 0.75,
  C: 1.0,
  B: 1.5,
  A: 2.5,
  S: 4.0,
};

export const ANMELDELSE_FORSINKELSE_MIN = 2;
export const ANMELDELSE_FORSINKELSE_MAX = 5;

export const SALG_DECAY = 0.05;
export const SALG_INGEN_ANMELDELSE_BOOST = 0.3;
export const SALG_DOEDDAGER = 7;

export const KAMPANJE_SALGSMULT_CAP = 4.0;
export const KAMPANJE_SALG_MILESTONE = 500;

export const MILESTONE_GRENSER: ReadonlyArray<{ tall: number; melding: string }> = [
  { tall: 100, melding: 'har nådd 100 eksemplarer' },
  { tall: 1_000, melding: 'er bestselger! (1 000 solgte)' },
  { tall: 10_000, melding: 'er nasjonal-storfilm! (10 000 solgte)' },
];

// Runde 48 (Sven): basis-antall nye manus i innboksen per dag senket fra
// 3 til 2. Sesong-modifikator (manusFrekvens) og bonuser kan fortsatt løfte
// dette enkelte dager — f.eks. vår (1.3×) gir 2×1.3≈3.
export const MANUS_PER_DAG = 2;

export const FASE_KOSTNAD: Record<'redigering' | 'design' | 'trykk', number> = {
  redigering: 5_000,
  design: 3_000,
  trykk: 2_000,
};

export const FASE_DAGER: Record<'redigering' | 'design' | 'trykk', number> = {
  redigering: 4,
  design: 2,
  trykk: 3,
};

export const STATUS_PILLE_FARGE: Record<ContractStatus, number> = {
  kontrahert: 0x9a9285,
  redigeres: 0x862736,
  redigert: 0xd9a14b,
  designes: 0x862736,
  designet: 0xd9a14b,
  utgis: 0x862736,
  utgitt: 0x3d5a4d,
};

export const COLORS = {
  beige: 0xf4ead5,
  beigeDark: 0xd9cdb0,
  wine: 0x862736,
  ink: 0x1a1a1a,
  oakLight: 0xd9b382,
  oakDark: 0x8b5e3c,
  blueSoft: 0xb8d4e3,
  yellowWarm: 0xf4d58d,
  ochre: 0xd9a14b,
  forest: 0x3d5a4d,
  white: 0xffffff,
  shadow: 0x000000,
} as const;

export const TIER_COLORS = {
  D: 0x6b6b6b,
  C: 0x8b5e3c,
  B: 0x862736,
  A: 0x4a2c4f,
  S: 0xb8860b,
} as const;

export const FONTS = {
  display: '"Playfair Display", Georgia, serif',
  body: '"DM Sans", system-ui, sans-serif',
  hud: '"Inter Tight", "DM Sans", system-ui, sans-serif',
} as const;

// Global font scale (runde 31): bump alle Phaser-fonter ~25 % for lesbarhet
// på faktiske mobil-enheter. Veldig liten font (<18px) løftes mer aggressivt
// til minst 22px så det aldri går under lesbarhets-gulvet. Display-fonter
// (>=72px) lar vi være — de er allerede store nok.
export const FONT_SCALE = 1.25;
export const FONT_MIN_PX = 22;

export function fontStr(px: number): string {
  if (px >= 72) return `${px}px`;
  const bump =
    px < 18
      ? Math.max(FONT_MIN_PX, Math.round(px * 1.5))
      : Math.round(px * FONT_SCALE);
  return `${bump}px`;
}

// Mikro-etiketter (2026-06-04): små versal-labels som «KRONER»/«DAG» er
// etiketter, ikke lesetekst — de skal ikke løftes til 22px-gulvet i
// `fontStr` (det doblet dem og sprengte trange soner som HUD-en). De
// skaleres med FONT_SCALE, men med et lavere gulv og uten ×1.5-spranget.
export const FONT_MICRO_MIN_PX = 14;

export function microFontStr(px: number): string {
  return `${Math.max(FONT_MICRO_MIN_PX, Math.round(px * FONT_SCALE))}px`;
}

export const ASSET_KEYS = {
  envRedaksjon: 'env-redaksjon',
  envDireksjon: 'env-direksjon',
  envBokhandel: 'env-bokhandel',
  envResepsjon: 'env-resepsjon',
  envMarked: 'env-marked',
  envDesign: 'env-design',
  envTrykkeri: 'env-trykkeri',
  envTakterrasse: 'env-takterrasse',
  playerSven: 'player-sven',
  // Etasje-ikoner (runde 39) — erstatter emoji i sidemeny
  iconEtasjeTak: 'icon-etasje-tak',
  iconEtasjeDireksjon: 'icon-etasje-direksjon',
  iconEtasjeRedaksjon: 'icon-etasje-redaksjon',
  iconEtasjeDesign: 'icon-etasje-design',
  iconEtasjeMarked: 'icon-etasje-marked',
  iconEtasjeResepsjon: 'icon-etasje-resepsjon',
  iconEtasjeBokhandel: 'icon-etasje-bokhandel',
  iconEtasjeKjeller: 'icon-etasje-kjeller',
  // FAB-ikoner (runde 39) — erstatter emoji i aksjons-FAB
  iconFabInbox: 'icon-fab-inbox',
  iconFabForfattere: 'icon-fab-forfattere',
  iconFabBokhandel: 'icon-fab-bokhandel',
  iconFabKaffekopp: 'icon-fab-kaffekopp',
  iconFabPalett: 'icon-fab-palett',
  iconFabUtenlands: 'icon-fab-utenlands',
  // Runde 44: tre siste FAB-ikoner som erstatter de gjenværende emoji-fallbackene
  iconFabMegafon: 'icon-fab-megafon',
  iconFabSkrivelab: 'icon-fab-skrivelab',
  iconFabAksjon: 'icon-fab-aksjon',
} as const;

export const ASSET_PATHS = {
  [ASSET_KEYS.envRedaksjon]: 'assets/environments/env_redaksjon_default_01.webp',
  [ASSET_KEYS.envDireksjon]: 'assets/environments/env_direksjon_default_01.webp',
  [ASSET_KEYS.envBokhandel]: 'assets/environments/env_bokhandel_default.webp',
  [ASSET_KEYS.envResepsjon]: 'assets/environments/env_resepsjon_default.webp',
  [ASSET_KEYS.envMarked]: 'assets/environments/env_marked_default.webp',
  [ASSET_KEYS.envDesign]: 'assets/environments/env_design_default.webp',
  [ASSET_KEYS.envTrykkeri]: 'assets/environments/env_trykkeri_default.webp',
  [ASSET_KEYS.envTakterrasse]: 'assets/environments/env_takterrasse_default.webp',
  [ASSET_KEYS.playerSven]: 'assets/characters/player_sven-default_01.webp',
  [ASSET_KEYS.iconEtasjeTak]: 'assets/ui/icon_etasje_tak.webp',
  [ASSET_KEYS.iconEtasjeDireksjon]: 'assets/ui/icon_etasje_direksjon.webp',
  [ASSET_KEYS.iconEtasjeRedaksjon]: 'assets/ui/icon_etasje_redaksjon.webp',
  [ASSET_KEYS.iconEtasjeDesign]: 'assets/ui/icon_etasje_design.webp',
  [ASSET_KEYS.iconEtasjeMarked]: 'assets/ui/icon_etasje_marked.webp',
  [ASSET_KEYS.iconEtasjeResepsjon]: 'assets/ui/icon_etasje_resepsjon.webp',
  [ASSET_KEYS.iconEtasjeBokhandel]: 'assets/ui/icon_etasje_bokhandel.webp',
  [ASSET_KEYS.iconEtasjeKjeller]: 'assets/ui/icon_etasje_kjeller.webp',
  [ASSET_KEYS.iconFabInbox]: 'assets/ui/icon_fab_inbox.webp',
  [ASSET_KEYS.iconFabForfattere]: 'assets/ui/icon_fab_forfattere.webp',
  [ASSET_KEYS.iconFabBokhandel]: 'assets/ui/icon_fab_bokhandel.webp',
  [ASSET_KEYS.iconFabKaffekopp]: 'assets/ui/icon_fab_kaffekopp.webp',
  [ASSET_KEYS.iconFabPalett]: 'assets/ui/icon_fab_palett.webp',
  [ASSET_KEYS.iconFabUtenlands]: 'assets/ui/icon_fab_utenlands.webp',
  [ASSET_KEYS.iconFabMegafon]: 'assets/ui/icon_fab_megafon.webp',
  [ASSET_KEYS.iconFabSkrivelab]: 'assets/ui/icon_fab_skrivelab.webp',
  [ASSET_KEYS.iconFabAksjon]: 'assets/ui/icon_fab_aksjon.webp',
} as const;

export const MORGEN_RUNDTUR_REKKEFOLGE = ['envResepsjon', 'envBokhandel', 'envDireksjon', 'envRedaksjon'] as const;
export const MORGEN_RUNDTUR_DURATION_PER_ETASJE = 380;

export const BYGG_INTRO_VIDEO = 'assets/intro/bygg-intro-v4.mp4';

export const ROOM_VIDEOS: Partial<Record<string, string>> = {
  redaksjon: 'assets/environments/env_redaksjon_loop.mp4',
  resepsjon: 'assets/environments/env_resepsjon_loop.mp4',
  bokhandel: 'assets/environments/env_bokhandel_loop.mp4',
  marked: 'assets/environments/env_marked_loop.mp4',
  design: 'assets/environments/env_design_loop.mp4',
  direksjon: 'assets/environments/env_direksjon_loop.mp4',
  kjeller: 'assets/environments/env_trykkeri_loop.mp4',
  tak: 'assets/environments/env_takterrasse_loop.mp4',
};

export const HISTORIKK_MAX_DAGER = 120;
export const HISTORIKK_GRAF_DAGER = 30;
export const LAV_KAPITAL_GRENSE = 50_000;
export const BESTSELGER_SALG_GRENSE = 10_000;

export const SJANGER_FARGER: Record<string, number> = {
  KRIM: 0x6b1a1f,
  LITERARY: 0x862736,
  BARNEBOK: 0xf4d58d,
  NONFICTION: 0x8b5e3c,
  POETRY: 0x6f4a8f,
  FANTASY: 0x2c3e6b,
  FEELGOOD: 0xe8a4b8,
  ROMANCE: 0xc94a4a,
};
export const SJANGER_FALLBACK_FARGE = 0x9a9285;
